import { QueryClient, QueryObserverOptions, useQuery, UseQueryOptions, UseQueryReturnType } from '@tanstack/vue-query';
import { AxiosError } from 'axios';
import { computed, effectScope, isRef, MaybeRef, Ref, unref, watch } from 'vue';

import LocalCacheKeys from '@/common/enums/local-cache-keys.enum';
import { Organization } from '@/common/models';
import OrganizationType from '@/common/models/memberships/OrganizationType.enum';
import { QueryParams } from '@/common/models/query-params';
import { waitUntil } from '@/utils/DelayHelper';
import LocalCache from '@/utils/LocalCache';

export const getOrganization = () => LocalCache.get(LocalCacheKeys.ORGANIZATION) as Organization;
export const getOrganizationType = () => getOrganization().type;
export const isCuratorOrExchange = () =>
  [OrganizationType.CURATOR, OrganizationType.EXCHANGE].includes(getOrganizationType());

interface Callbacks<TData, TError> {
  onSuccess?: (data: TData) => void;
  onError?: (error: TError) => void;
}

type QueryOptions<T> = Omit<QueryObserverOptions<T>, 'queryKey' | 'queryFn' | 'staleTime' | 'retry'>;
interface QuerySettings<T> extends QueryOptions<T> {
  mutation?: (data: Array<T>) => void;
  onSuccess?: (data: IGetAllAPIResult<T> | Array<T>) => void;
  onError?: (error: Error) => void;
  queryParams?: QueryParams;
}

interface IGetAllAPIResult<T> {
  items: T[];
}

export interface IQuery<ReturnType> {
  query: UseQueryReturnType<ReturnType, Error>;
}

export interface IQueryAndItems<T> extends IQuery<IGetAllAPIResult<T>> {
  query: UseQueryReturnType<IGetAllAPIResult<T>, Error>;
  items: Ref<T[]>;
}

export interface IQueryAndItem<T> extends IQuery<T> {
  query: UseQueryReturnType<T, Error>;
  item: Ref<T>;
}

const queryClient = new QueryClient();

export default queryClient;

export function clearCachedQueries() {
  queryClient.clear();
}

export function resetQueries(queryKey: string[]) {
  setTimeout(async () => {
    await queryClient.resetQueries({ queryKey, exact: true });
  }, 1000);
}

export const useQueryEvents = <TData, TError>(
  query: UseQueryReturnType<TData, TError>,
  callbacks: Callbacks<TData, TError>,
) => {
  const { onSuccess, onError } = callbacks;

  const sendCallbacks = (status: 'success' | 'error' | 'pending') => {
    if (status === 'success') {
      onSuccess?.(query.data.value as TData);
    } else if (status === 'error') {
      onError?.(query.error.value as TError);
    }
  };

  watch(query.status, sendCallbacks, { immediate: true });
};

function retry(failureCount: number, error: Error): boolean {
  const retryOnStatusCodes = [502, 503, 504];
  if (retryOnStatusCodes.includes((error as AxiosError)?.response?.status)) return failureCount < 3; // Retry up to 3 times
  return false;
}

export function getCachedData<T>(queryKey: Array<T>) {
  return queryClient.getQueryData(queryKey);
}

type GetQueryFunction<T> = (queryClient?: QueryClient, optionsOverride?: QuerySettings<T>) => IQueryAndItems<T>;

export function createGetQuery<T>(
  queryKey: Array<string>,
  queryFn: (qp?: QueryParams) => Promise<IGetAllAPIResult<T> | Array<T>>,
  settings: QuerySettings<T>,
): GetQueryFunction<T> {
  return function (queryClient = undefined, settingsOverride: QuerySettings<T> = {}) {
    const { mutation, onSuccess, onError, queryParams, ...queryOptions } = { ...settings, ...settingsOverride };

    const query = useQuery<IGetAllAPIResult<T> | Array<T>>(
      {
        queryKey: [...queryKey, ...(queryParams ? [queryParams.toQueryString()] : [])],
        queryFn: () => queryFn(queryParams),
        staleTime: 1000 * 60 * 60, // 1h
        retry,
        ...queryOptions,
      },
      queryClient,
    );

    useQueryEvents(query, {
      onSuccess: (data) => {
        const results = 'items' in data ? data.items : data;
        mutation?.(results);
        onSuccess?.(data);
      },
      onError: (error) => {
        mutation?.([]);
        onError?.(error);
      },
    });
    const items = computed(() =>
      query.status.value === 'success'
        ? (query.data.value as IGetAllAPIResult<T>).items || (query.data.value as IGetAllAPIResult<T>) || new Array<T>()
        : [],
    );

    return <IQueryAndItems<T>>{
      query,
      items,
    };
  };
}

type GetAllQueryFunction<T> = (queryClient?: QueryClient, queryParams?: MaybeRef<QueryParams>) => IQueryAndItems<T>;

export function createGetAllQuery<T>(
  queryKey: Array<string>,
  queryFn: (qp?: QueryParams) => Promise<IGetAllAPIResult<T> | Array<T>>,
  mutation: (data: Array<T>) => void = null,
  preMutationTransform: (data: Array<T>) => Array<T> = (data) => data,
): GetAllQueryFunction<T> {
  return function (
    queryClient = undefined,
    queryParams?: MaybeRef<QueryParams>,
    queryOptions: QueryOptions<IGetAllAPIResult<T> | Array<T>> = {},
  ) {
    const queryKeyWithParams = computed(() => {
      return [...queryKey, ...(queryParams ? [unref(queryParams).toQueryString()] : [])];
    });

    const useQueryOptions: UseQueryOptions<IGetAllAPIResult<T> | Array<T>> = {
      queryKey: queryKeyWithParams,
      queryFn: () => queryFn(unref(queryParams)),
      staleTime: 1000 * 60 * 60, // 1h
      retry,
      ...queryOptions,
    };
    const query = useQuery<IGetAllAPIResult<T> | Array<T>>(useQueryOptions, queryClient);

    useQueryEvents(query, {
      onSuccess: (items) => {
        if (mutation) {
          mutation(Array.isArray(items) ? items : (items as IGetAllAPIResult<T>).items);
        }
      },
      onError: () => {
        if (mutation) {
          mutation([]);
        }
      },
    });
    const items = computed(() =>
      query.status.value === 'success'
        ? preMutationTransform(
            (query.data.value as IGetAllAPIResult<T>).items || (query.data.value as Array<T>) || new Array<T>(),
          )
        : [],
    );

    return <IQueryAndItems<T>>{
      query,
      items,
    };
  };
}

type GetOneQueryFunction<T> = (id: MaybeRef<string | number>, queryClient?: QueryClient) => IQueryAndItem<T>;

export function createGetOneQuery<T>(
  queryKey: Array<string>,
  queryFn: (id: string | number) => Promise<T>,
): GetOneQueryFunction<T> {
  return function (id: MaybeRef<string | number>, queryClient = undefined) {
    const query = useQuery<T>(
      {
        queryKey: [...queryKey, id],
        queryFn: () => queryFn(unref(id)),
        staleTime: 1000 * 60 * 60, // 1h
        retry,
        enabled: computed(() => !!unref(id)),
      },
      queryClient,
    );

    const item = computed(() => {
      return query.status.value === 'success' ? query.data.value : null;
    });

    return <IQueryAndItem<T>>{
      query,
      item,
    };
  };
}

/**
 * Remove queries that include extra parameters than just the query key.
 * Example
 * 1. I start by opening a specific deal page that contains demographic ids [1, 2]
 * 2. I decide to add additional demographics or create a new deal with some.
 * 3. When I open the create/edit view, a call will be made to retrieve all demographics.
 *    - In this case, I would want to clear the cache of any queries that were made for specific IDs, since we would now have all of them in the cache.
 * @param queryKey
 */
export function removeQueriesWithExtraParams(queryKey: string[]) {
  const queriesWithKey = queryClient.getQueriesData({ queryKey });
  if (!queriesWithKey || queriesWithKey.length === 0) return;
  queriesWithKey.forEach(([currentQueryKey]) => {
    if (currentQueryKey.length > queryKey.length) {
      queryClient.removeQueries({ queryKey: currentQueryKey, exact: true });
    }
  });
}

export async function waitForPendingQueries(queries?: IQuery<any>[]) {
  if (!(queries instanceof Array)) console.error('waitForPendingQueries: queries is not an array');
  await waitUntil(() => {
    return queries.every((x) =>
      isRef(x.query.status)
        ? ['error', 'success'].includes(x.query.status.value)
        : ['error', 'success'].includes(x.query.status),
    );
  });
}

/**
 * Run a query in a vue scope and return query's result
 * To be used outside of components (e.g. in services, models, etc.)
 */
export async function runQueryInScope<T>(useFunction: GetQueryFunction<T> | GetAllQueryFunction<T>): Promise<T[]> {
  const scope = effectScope();
  const query = scope.run(() => useFunction(queryClient));
  await waitForPendingQueries([query]);
  const result = query.items.value || [];
  scope.stop();
  return result;
}

/**
 * Peek at a query in a vue scope and return query's result
 * To be used outside of components (e.g. in services, models, etc.)
 */
export function peekQueryInScope<T>(useFunction: GetQueryFunction<T> | GetAllQueryFunction<T>): T[] {
  const scope = effectScope();
  const query = scope.run(() => useFunction(queryClient));
  const result = query.items.value || [];
  scope.stop();
  return result;
}
